import { Permission } from "components/shareSection/entities";
import { axiosInstance } from "contexts/axiosApiInstance";
import { createLogger } from "debug/DebugingLogger";
import { wasmBody } from "components/common/wasmBody";
import {
  Notification,
  NotificationPreferences,
  NotificationStatus,
  UpdateNotificationBody,
  UpdatePreferencesBody,
} from "target_wasm";
const log = createLogger("Notifications APIs");

export const fetchNotifications = async (): Promise<Notification[]> => {
  try {
    const response = await axiosInstance.get(`/action/notifications`, {
      responseType: "text",
    });
    const data: string =
      typeof response.data === "string"
        ? response.data
        : JSON.stringify(response.data || []);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- WASM interop: static method not in TS declarations
    const notifications: Notification[] = (Notification as any).new_from_js(
      data,
    );
    return notifications;
  } catch (error) {
    log.error("Failed to fetch notifications", error);
    throw new Error("Failed to fetch notifications");
  }
};

export const updateNotificationStatus = async (
  request: UpdateNotificationBody,
): Promise<boolean> => {
  try {
    const body = wasmBody(request);
    const response = await axiosInstance.post(
      "/action/update_notification",
      body,
    );
    log.info("Notification status updated successfully");
    return response.data === "Updated";
  } catch (error) {
    log.error("Failed to update notification status", error);
    throw new Error("Failed to update notification status");
  }
};

export const fetchNotificationPreferences =
  async (): Promise<NotificationPreferences> => {
    try {
      const response = await axiosInstance.get(`/action/get_preferences`);
      const preferences = NotificationPreferences.new_js(
        response.data.user_id,
        response.data.in_email_notification,
        response.data.enabled,
      );
      return preferences;
    } catch (error) {
      log.error("Failed to fetch notification preferences", error);
      throw new Error("Failed to fetch notification preferences");
    }
  };

export const setNotificationPreferences = async (
  preferences: UpdatePreferencesBody,
): Promise<boolean> => {
  try {
    const body = wasmBody(preferences);
    const response = await axiosInstance.post("/action/set_preferences", body);
    log.info("Notification preferences updated successfully");
    return response.data === "Updated";
  } catch (error) {
    log.error("Failed to update notification preferences", error);
    throw new Error("Failed to update notification preferences");
  }
};

export const acceptTableRequest = async (
  tableId: string,
  tableName: string,
  modelId: string,
  tableVersion: number,
  identifier: string,
): Promise<boolean> => {
  try {
    const isTeam = identifier.startsWith("team-");
    const data = {
      users: !isTeam ? [identifier] : [],
      teams: isTeam ? [identifier] : [],
      options: Permission.CanImport,
      table_id: tableId,
      table_name: tableName,
      accept_access_request: true,
      model_request: modelId,
    };

    const response = await axiosInstance.post("/action/share_table", data);
    log.info("Table request accepted successfully");
    return response.status === 200;
  } catch (error) {
    log.error("Failed to accept table request", error);
    throw new Error("Failed to accept table request");
  }
};

export const declineTableRequest = async (
  tableId: string,
  tableName: string,
  targetModelId: string,
  requesterId: string,
  valuesOnly: boolean,
): Promise<boolean> => {
  try {
    const data = {
      requester_id: requesterId,
      table_id: tableId,
      table_name: tableName,
      target_model_id: targetModelId,
      // D37: declining a data-access ask must not unimport — its target
      // model holds someone else's live import.
      values_only: valuesOnly,
    };

    const response = await axiosInstance.post(
      "/action/decline_table_access",
      data,
    );
    log.info("Table request declined successfully");
    return response.status === 200;
  } catch (error) {
    log.error("Failed to decline table request", error);
    throw new Error("Failed to decline table request");
  }
};

export const respondToTransferOffer = async (
  notificationId: string,
  action:
    | { type: "Accept"; to_model: string }
    | { type: "Reject" }
    | { type: "Cancel"; recipient_id: string },
): Promise<boolean> => {
  try {
    const response = await axiosInstance.post(
      "/action/ownership_transfer/table/respond",
      { notification_id: notificationId, action },
    );
    return response.status === 200;
  } catch (error) {
    log.error("Failed to respond to transfer offer", error);
    throw new Error("Failed to respond to transfer offer");
  }
};

export const markNotificationAsSeen = async (
  notificationId: string,
): Promise<void> => {
  try {
    const updateRequest = UpdateNotificationBody.new_js(
      notificationId,
      NotificationStatus.Seen,
    );
    await updateNotificationStatus(updateRequest);
  } catch (error) {
    log.error("Failed to mark notification as seen:", error);
  }
};

export const markAllNotificationsAsRead = async (): Promise<boolean> => {
  try {
    const response = await axiosInstance.post(
      "/action/mark_all_notifications_as_seen",
    );
    log.info("All notifications marked as read successfully");
    return response.status === 200;
  } catch (error) {
    log.error("Failed to mark all notifications as read", error);
    throw new Error("Failed to mark all notifications as read");
  }
};
